import React, { Component } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Row,
  Col,
  UncontrolledTooltip
} from "reactstrap";
import MembresiaIntegranteEliminado from "../../../../types/MembresiaIntegranteEliminado";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import ConfirmModal from "../../common/confirmModal/confirmModal";
import s from "./styles.scss";
import { Styled } from "../../../../toolkit/decorators/Artifact";

interface Props {
  eliminado: MembresiaIntegranteEliminado;
  onReasignarCargo?: (
    idCotizacionItem: number,
    idMembresiaSocioPersona: number
  ) => Promise<any>;
  processingReasignacion?: boolean;
}

interface State {
  confirmModalOpen: boolean;
  selectedIntegranteId: number | null;
  selectedIntegranteNombre: string;
}

@Styled(s)
export default class IntegranteResolucionEliminado extends Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = {
      confirmModalOpen: false,
      selectedIntegranteId: null,
      selectedIntegranteNombre: ""
    };
  }

  handleOpenConfirmModal = (
    idMembresiaSocioPersona: number,
    nombreIntegrante: string
  ) => {
    this.setState({
      confirmModalOpen: true,
      selectedIntegranteId: idMembresiaSocioPersona,
      selectedIntegranteNombre: nombreIntegrante
    });
  };

  handleCancelReasignacion = () => {
    this.setState({
      confirmModalOpen: false,
      selectedIntegranteId: null,
      selectedIntegranteNombre: ""
    });
  };

  handleConfirmReasignacion = async () => {
    const { eliminado, onReasignarCargo } = this.props;
    const { selectedIntegranteId } = this.state;

    if (onReasignarCargo && selectedIntegranteId) {
      await onReasignarCargo(eliminado.idCotizacionItem, selectedIntegranteId);
      this.setState({
        confirmModalOpen: false,
        selectedIntegranteId: null,
        selectedIntegranteNombre: ""
      });
    }
  };

  renderBotonReasignacion = (integrante: any) => {
    const { eliminado } = this.props;
    const isTitular = integrante.tipoSocio === "TITULAR";
    const isPagado = integrante.cotizacionEstatus === "PAGADO";
    const isDisabled = isTitular || isPagado;

    let tooltip = "";
    if (isTitular) {
      tooltip = "No se puede asociar el cargo al titular";
    } else if (isPagado) {
      tooltip = "Ya existe pago para este integrante";
    } else {
      tooltip = `Reasignar este cargo eliminado a: ${integrante.integrante}`;
    }

    const buttonId = `asociar-integrante-${eliminado.idCotizacionItem}-${integrante.idMembresiaSocioPersona}`;

    return (
      <>
        <span
          id={buttonId}
          style={{
            cursor: "pointer",
            opacity: isDisabled ? 0.8 : 1
          }}
          onClick={() => {
            if (!isDisabled) {
              this.handleOpenConfirmModal(
                integrante.idMembresiaSocioPersona,
                integrante.integrante
              );
            }
          }}
        >
          <FontAwesomeIcon
            icon={faUserPlus}
            color={isDisabled ? "#989a9c" : "#28a745"}
          />
        </span>
        <UncontrolledTooltip placement="left" target={buttonId}>
          {tooltip}
        </UncontrolledTooltip>
      </>
    );
  };

  render() {
    const { eliminado } = this.props;

    return (
      <Card className="mb-3">
        {/* Descripción del cargo */}
        <CardHeader>
          <span
            style={{
              color: "#8b0000",
              fontWeight: "bold"
            }}
          >
            Cargo Eliminado: {eliminado.descripcion}
          </span>
        </CardHeader>

        {/* Contenido del eliminado */}
        <CardBody>
          {/* Datos del Cargo */}
          <Row>
            <Col md={12}>
              <h5 className="mb-2">Datos del Cargo</h5>
              <Table size="sm" bordered>
                <thead className="thead-light">
                  <tr>
                    <th className="w88">Cotización</th>
                    <th className="w96">Periodo</th>
                    <th className="w88">Factura</th>
                    <th className="w96 text-center">Importe</th>
                    <th className="w72">No. Emp.</th>
                    <th>Empleado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{eliminado.idCotizacionItem}</td>
                    <td>{formatIsoDate(eliminado.fechaPeriodo)}</td>
                    <td>{eliminado.folioFactura}</td>
                    <td className="text-center">
                      {formatMoney(eliminado.importe)}
                    </td>
                    <td>{eliminado.asesorNumeroEmpleado || "N/A"}</td>
                    <td>{eliminado.asesorNombre || "N/A"}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>

          {/* Datos del Integrante Eliminado */}
          <Row className="mt-3">
            <Col md={12}>
              <h5 className="mb-2">Datos del Integrante Eliminado</h5>
              <Table size="sm" bordered>
                <thead className="thead-light">
                  <tr>
                    <th className="w88">Persona</th>
                    <th className="w88">Id Interno</th>
                    <th>Nombre</th>
                    <th className="w200">Membresía del Cargo</th>
                    <th className="w200">Membresía Actual</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{eliminado.idPersona}</td>
                    <td>{eliminado.idMembresiaSocioPersona}</td>
                    <td>{eliminado.nombreCompleto}</td>
                    <td>
                      {eliminado.idMembresiaSocioCargo ? (
                        <a
                          href={`/membresias-socios/${eliminado.idMembresiaSocioCargo}?idClub=${eliminado.idClubBaseCargo}&idPublico=${eliminado.idPublicoCargo}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {eliminado.idPublicoCargo} de{" "}
                          {eliminado.clubBaseCargo}
                        </a>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>
                      {eliminado.idMembresiaSocioActual ? (
                        <a
                          href={`/membresias-socios/${eliminado.idMembresiaSocioActual}?idClub=${eliminado.idClubBaseActual}&idPublico=${eliminado.idPublicoActual}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {eliminado.idPublicoActual} de{" "}
                          {eliminado.clubBaseActual}
                        </a>
                      ) : (
                        <span className="text-muted">Sin membresía actual</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>

          {/* Tabla con datos de precálculo */}
          {!!eliminado.idPrecalculo && (
            <Row className="mt-3">
              <Col md={12}>
                <h5 className="mb-2">Datos de Precálculo</h5>
                <p className="mb-2">
                  El cargo eliminado ya pasó por un proceso de precálculo, pero
                  no se pagó.
                </p>
                <Table size="sm" bordered>
                  <tbody>
                    <tr>
                      <th className="w256">Cuenta para alcance</th>
                      <td>{eliminado.cuentaAlcance ? "Sí" : "No"}</td>
                    </tr>
                    {eliminado.razonesNoAlcance &&
                      eliminado.razonesNoAlcance.length > 0 && (
                        <tr>
                          <th className="w256">
                            Razones por las que no contó en el alcance
                          </th>
                          <td>
                            {eliminado.razonesNoAlcance.map((r, i) => (
                              <div key={i}>- {r}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                    {eliminado.razonesNoCumpleReglas &&
                      eliminado.razonesNoCumpleReglas.length > 0 && (
                        <tr>
                          <th className="w256">
                            Razones por las que no se pagó
                          </th>
                          <td>
                            {eliminado.razonesNoCumpleReglas.map((r, i) => (
                              <div key={i}>- {r}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}

          {/* Tabla con integrantes disponibles para asociar */}
          {eliminado.integrantes && eliminado.integrantes.length > 0 && (
            <Row className="mt-3">
              <Col md={12}>
                <h5 className="mb-2">Integrantes Disponibles para Asociar</h5>
                <p className="mb-2">
                  Seleccione un integrante para asociar este cargo eliminado.
                </p>
                <Table size="sm" bordered>
                  <thead className="thead-light">
                    <tr>
                      <th className="w88">Persona</th>
                      <th className="w88">Id Interno</th>
                      <th>Nombre</th>
                      <th className="w140">Tipo Socio</th>
                      <th className="w140">Tipo Integrante</th>
                      <th className="w100">Estatus</th>
                      <th className="w100 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eliminado.integrantes.map((integrante, index) => (
                      <tr key={index}>
                        <td>{integrante.idIntegrante}</td>
                        <td>{integrante.idMembresiaSocioPersona}</td>
                        <td>{integrante.integrante}</td>
                        <td>{integrante.tipoSocio}</td>
                        <td>{integrante.tipoIntegrante}</td>
                        <td>{integrante.integranteEstatus}</td>
                        <td className="text-center">
                          {this.renderBotonReasignacion(integrante)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}
        </CardBody>

        {/* Modal de Confirmación */}
        <ConfirmModal
          isOpen={this.state.confirmModalOpen}
          title={`Confirme la reasignación del cargo`}
          message={`¿Está seguro de reasignar este cargo eliminado al integrante "${this.state.selectedIntegranteNombre}"?`}
          ok={this.handleConfirmReasignacion}
          cancel={this.handleCancelReasignacion}
          size="md"
          waiting={this.props.processingReasignacion}
        />
      </Card>
    );
  }
}
